import React, { useRef, useCallback } from 'react';
import {
  AlertTriangle,
  Users,
  Box,
  Layers,
  FileText,
  Brain,
  BarChart3,
  Database,
  ShieldCheck,
  User,
} from 'lucide-react';

// Virtual canvas the whole diagram is designed against. Every node below
// is positioned as a percentage of this canvas, and the SVG connector
// layer shares the same viewBox — so everything scales together as one
// proportional unit, at any container width.
const CANVAS_W = 1000;
const CANVAS_H = 860;
const px = (v) => `${((v / CANVAS_W) * 100).toFixed(3)}%`;
const py = (v) => `${((v / CANVAS_H) * 100).toFixed(3)}%`;

const CATEGORIES = [
  { icon: Layers, label: 'Incidents', x: 140 },
  { icon: FileText, label: 'Solutions', x: 320 },
  { icon: Brain, label: 'Patterns', x: 500 },
  { icon: Users, label: 'Feedback', x: 680 },
  { icon: BarChart3, label: 'Analytics', x: 860 },
];
const CAT_ICON_SIZE = 76;
const CAT_TOP = 575;

const HeroVisual = () => {
  const wrapRef = useRef(null);
  const frameRef = useRef(null);

  // Reactive 3D tilt: the diagram gently leans toward the cursor, like a
  // physical panel of glass floating in the scene. Updates are written
  // straight to CSS custom properties via ref (no re-render) and batched
  // in a rAF for smoothness.
  const handleMouseMove = useCallback((e) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width; // 0..1
    const py = (e.clientY - rect.top) / rect.height; // 0..1

    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(() => {
      const rotateY = (px - 0.5) * 14; // left/right lean
      const rotateX = (0.5 - py) * 12; // up/down lean
      el.style.setProperty('--tilt-x', `${rotateX.toFixed(2)}deg`);
      el.style.setProperty('--tilt-y', `${rotateY.toFixed(2)}deg`);
      el.style.setProperty('--glow-x', `${(px * 100).toFixed(1)}%`);
      el.style.setProperty('--glow-y', `${(py * 100).toFixed(1)}%`);
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    el.style.setProperty('--tilt-x', '0deg');
    el.style.setProperty('--tilt-y', '0deg');
    el.style.setProperty('--glow-x', '50%');
    el.style.setProperty('--glow-y', '40%');
  }, []);

  return (
    <div
      className="hero-orb-wrap"
      ref={wrapRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div className="solution-diagram">
        <div className="sd-canvas">
          {/* Connector layer */}
          <svg
            className="sd-svg"
            viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
          >
            {/* Incident -> core */}
            <path className="sd-flow-line" d="M500,145 L500,180" />
            {/* Core -> small diamond */}
            <path className="sd-flow-line" d="M500,360 L500,395" />
            {/* Small diamond -> fan start */}
            <path className="sd-flow-line" d="M500,470 L500,515" />
            {/* Fan-out curves to each category icon */}
            {CATEGORIES.map((c) => (
              <path
                key={c.label}
                className="sd-flow-line"
                d={`M500,515 C500,548 ${c.x},548 ${c.x},575`}
              />
            ))}
            {/* Converging curves into the Knowledge Base */}
            <path className="sd-flow-line-soft" d="M140,651 C140,682 400,682 400,700" />
            <path className="sd-flow-line-soft" d="M860,651 C860,682 600,682 600,700" />
            <path className="sd-flow-line" d="M500,651 L500,700" />

            {/* Flow dots */}
            <circle className="sd-flow-dot" cx="500" cy="180" r="4.5" />
            <circle className="sd-flow-dot" cx="500" cy="395" r="4.5" />
            <circle className="sd-flow-dot" cx="500" cy="515" r="4.5" />
            {CATEGORIES.map((c) => (
              <circle key={`dot-${c.label}`} className="sd-flow-dot" cx={c.x} cy="575" r="3.5" />
            ))}
            <circle className="sd-flow-dot" cx="500" cy="700" r="4.5" />
          </svg>

          {/* Incident Detected */}
          <div
            className="sd-node"
            style={{ left: px(350), top: py(20), width: px(300), height: py(125) }}
          >
            <div className="sd-panel">
              <div className="sd-panel-head">
                <div className="sd-alert-wrap">
                  <div className="sd-alert-ring" />
                  <div className="sd-panel-icon sd-panel-icon-warning">
                    <AlertTriangle />
                  </div>
                </div>
                <h3 className="sd-panel-title">Incident Detected</h3>
              </div>
              <p className="sd-panel-body">Captured from systems and users</p>
            </div>
          </div>

          {/* Core node */}
          <div
            className="sd-node sd-core-wrap"
            style={{ left: px(405), top: py(170), width: px(190), height: py(190) }}
          >
            <div className="sd-core-ring" />
            <div className="sd-core">
              <div className="sd-core-diamond" />
            </div>
          </div>

          {/* AI Agent Team */}
          <div
            className="sd-node"
            style={{ left: px(640), top: py(175), width: px(330), height: py(205) }}
          >
            <div className="sd-panel">
              <div className="sd-panel-head">
                <div className="sd-panel-icon">
                  <Users />
                </div>
                <h3 className="sd-panel-title">AI Agent Team</h3>
              </div>
              <p className="sd-panel-body">Multi-agent collaboration</p>
              <p className="sd-panel-body sd-panel-accent">+ Expert Approval</p>
              <div className="sd-avatars">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="sd-avatar">
                    <User />
                  </div>
                ))}
                <div className="sd-avatar sd-avatar-check">
                  <ShieldCheck />
                </div>
              </div>
            </div>
          </div>

          {/* Small diamond node */}
          <div
            className="sd-node sd-core-wrap"
            style={{ left: px(460), top: py(390), width: px(80), height: py(80) }}
          >
            <div className="sd-core" style={{ width: '100%', height: '100%' }}>
              <div className="sd-core-diamond" />
            </div>
          </div>

          {/* Permanent Knowledge Block */}
          <div
            className="sd-node"
            style={{ left: px(70), top: py(345), width: px(330), height: py(140) }}
          >
            <div className="sd-panel">
              <div className="sd-panel-head">
                <div className="sd-panel-icon">
                  <Box />
                </div>
                <h3 className="sd-panel-title">Permanent Knowledge Block</h3>
              </div>
              <p className="sd-panel-body">Validated insights are stored as permanent knowledge</p>
            </div>
          </div>

          {/* Category row */}
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            return (
              <div
                key={c.label}
                className="sd-node sd-cat"
                style={{
                  left: px(c.x - CAT_ICON_SIZE / 2),
                  top: py(CAT_TOP),
                  width: px(CAT_ICON_SIZE),
                }}
              >
                <div className="sd-cat-icon">
                  <Icon />
                </div>
                <span className="sd-cat-label">{c.label}</span>
              </div>
            );
          })}

          {/* Compounding Knowledge Base */}
          <div
            className="sd-node"
            style={{ left: px(300), top: py(700), width: px(400), height: py(140) }}
          >
            <div className="sd-panel">
              <div className="sd-panel-head">
                <div className="sd-panel-icon">
                  <Database />
                </div>
                <h3 className="sd-panel-title">Compounding Knowledge Base</h3>
              </div>
              <p className="sd-panel-body">Every insight makes the system smarter</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroVisual;
